import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, LOGO_URL } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Pencil, Trash2, ExternalLink, LogOut, Loader2, Users, FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  title: "",
  subtitle: "",
  excerpt: "",
  hero_image: "",
  content: "",
  meta_title: "",
  meta_description: "",
  tags: "",
  status: "published",
  slug: "",
  intro_cta_text: "",
  intro_cta_url: "",
  mid_cta_text: "",
  mid_cta_url: "",
  end_cta_text: "",
  end_cta_url: "",
};

const toPayload = (f) => ({
  title: f.title,
  subtitle: f.subtitle,
  excerpt: f.excerpt,
  hero_image: f.hero_image,
  content: f.content,
  meta_title: f.meta_title,
  meta_description: f.meta_description,
  tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
  status: f.status,
  slug: f.slug || undefined,
  intro_cta: { text: f.intro_cta_text, url: f.intro_cta_url },
  mid_cta: { text: f.mid_cta_text, url: f.mid_cta_url },
  end_cta: { text: f.end_cta_text, url: f.end_cta_url },
});

const fromArticle = (a) => ({
  title: a.title || "",
  subtitle: a.subtitle || "",
  excerpt: a.excerpt || "",
  hero_image: a.hero_image || "",
  content: a.content || "",
  meta_title: a.meta_title || "",
  meta_description: a.meta_description || "",
  tags: (a.tags || []).join(", "),
  status: a.status || "published",
  slug: a.slug || "",
  intro_cta_text: a.intro_cta?.text || "",
  intro_cta_url: a.intro_cta?.url || "",
  mid_cta_text: a.mid_cta?.text || "",
  mid_cta_url: a.mid_cta?.url || "",
  end_cta_text: a.end_cta?.text || "",
  end_cta_url: a.end_cta?.url || "",
});

const FieldRow = ({ label, hint, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 py-5 border-b border-slate-100">
    <div className="md:col-span-3">
      <div className="text-sm font-semibold text-[#0B2C5B]">{label}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
    <div className="md:col-span-9">{children}</div>
  </div>
);

const inputBase = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#0B2C5B] outline-none focus:border-[#2AB7E8] focus:ring-2 focus:ring-[#2AB7E8]/20 transition";

const AdminArticles = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = list, "new" = create, otherwise id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/articles");
      setArticles(res.data);
    } catch {
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId("new");
    setForm(emptyForm);
  };

  const startEdit = async (id) => {
    try {
      const res = await api.get(`/admin/articles/${id}`);
      setForm(fromArticle(res.data));
      setEditingId(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Failed to load article");
    }
  };

  const cancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editingId === "new") {
        await api.post("/admin/articles", payload);
        toast.success("Article created");
      } else {
        await api.put(`/admin/articles/${editingId}`, payload);
        toast.success("Article updated");
      }
      cancel();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Article deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const isEditing = editingId !== null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-articles-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container-site flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Visit Bedford" className="h-12 w-auto" />
            <div className="hidden sm:block">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">Admin Console</div>
              <div className="font-serif-display text-lg font-bold text-[#0B2C5B] leading-none">Visit Bedford</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-slate-600">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-articles-logout" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 transition">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
        {/* Sub nav */}
        <div className="container-site flex items-center gap-2 pb-3">
          <Link to="/admin" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            <Users className="w-4 h-4" /> Subscribers
          </Link>
          <Link to="/admin/articles" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#E6F6FD] text-[#0B2C5B]">
            <FileText className="w-4 h-4" /> Articles
          </Link>
        </div>
      </header>

      <main className="container-site py-10 md:py-12">
        {!isEditing ? (
          <>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-2">Content</div>
                <h1 className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B2C5B] leading-tight">
                  Articles &amp; Guides
                </h1>
                <p className="text-slate-600 mt-2">{articles.length} total · drafts are hidden from the public site.</p>
              </div>
              <button onClick={startCreate} data-testid="admin-new-article-btn" className="btn-primary-gradient text-white rounded-full px-6 py-3 font-semibold shadow-md hover:-translate-y-0.5 transition-all inline-flex items-center gap-2 self-start md:self-auto">
                <Plus className="w-4 h-4" /> New Article
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {loading ? (
                <div className="p-16 flex items-center justify-center text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading articles…
                </div>
              ) : articles.length === 0 ? (
                <div className="p-16 text-center text-slate-500">
                  <FileText className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                  <p className="font-semibold text-[#0B2C5B] mb-1">No articles yet</p>
                  <p className="text-sm">Click "New Article" to publish your first guide.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-6 py-4 font-semibold">Title</th>
                        <th className="text-left px-6 py-4 font-semibold">Slug</th>
                        <th className="text-left px-6 py-4 font-semibold">Status</th>
                        <th className="text-left px-6 py-4 font-semibold">Tags</th>
                        <th className="text-right px-6 py-4 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articles.map((a) => (
                        <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                          <td className="px-6 py-4 font-medium text-[#0B2C5B] max-w-md">
                            <div className="line-clamp-1">{a.title}</div>
                          </td>
                          <td className="px-6 py-4 text-slate-600 font-mono text-xs">{a.slug}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-2.5 py-1 text-xs rounded-full font-medium ${a.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                              {a.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-slate-500 text-xs">{(a.tags || []).slice(0, 2).join(", ")}</td>
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            {a.status === "published" && (
                              <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate-500 hover:text-[#0B2C5B] text-xs font-semibold mr-3">
                                <ExternalLink className="w-3.5 h-3.5" /> View
                              </a>
                            )}
                            <button onClick={() => startEdit(a.id)} data-testid={`edit-article-${a.slug}`} className="inline-flex items-center gap-1 text-[#0B2C5B] hover:text-[#FF8C00] text-xs font-semibold mr-3">
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button onClick={() => remove(a.id)} className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-semibold">
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <button onClick={cancel} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0B2C5B] mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to articles
            </button>
            <h1 className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B2C5B] leading-tight mb-2">
              {editingId === "new" ? "New article" : "Edit article"}
            </h1>
            <p className="text-slate-600 mb-8">Fields marked with * are required. Content supports Markdown.</p>

            <form onSubmit={save} data-testid="admin-article-form" className="bg-white rounded-2xl border border-slate-200 p-6 md:p-10">
              <FieldRow label="Title *" hint="Used as the H1 and appears in the article hero">
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputBase} data-testid="article-title-input" />
              </FieldRow>
              <FieldRow label="Subtitle" hint="Optional italic line under the title">
                <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputBase} />
              </FieldRow>
              <FieldRow label="Slug" hint="Leave blank to auto-generate from title">
                <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated-slug" className={inputBase} />
              </FieldRow>
              <FieldRow label="Hero image URL *" hint="Use a high-resolution landscape image (1600×1000+)">
                <input required value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} placeholder="https://…" className={inputBase} />
                {form.hero_image && (
                  <img src={form.hero_image} alt="preview" className="mt-3 h-32 w-full md:w-72 rounded-lg object-cover border border-slate-200" />
                )}
              </FieldRow>
              <FieldRow label="Excerpt *" hint="Shown on /blog and at the top of the article">
                <textarea required rows={3} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputBase} />
              </FieldRow>
              <FieldRow label="Content (Markdown) *" hint="Use ## for H2, ### for H3, ** for bold, - for lists">
                <textarea required rows={16} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`${inputBase} font-mono text-xs leading-relaxed`} data-testid="article-content-input" />
              </FieldRow>
              <FieldRow label="Tags" hint="Comma-separated. Used for related articles.">
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="hotels, family-travel, universal-studios" className={inputBase} />
              </FieldRow>

              <div className="py-5 border-b border-slate-100">
                <div className="text-sm font-semibold text-[#0B2C5B] mb-1">SEO</div>
                <div className="text-xs text-slate-500 mb-4">Used by search engines and social sharing.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} placeholder="Meta title (60 chars max)" className={inputBase} />
                  <input value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} placeholder="Meta description (160 chars max)" className={inputBase} />
                </div>
              </div>

              <div className="py-5 border-b border-slate-100">
                <div className="text-sm font-semibold text-[#0B2C5B] mb-1">Inline CTAs</div>
                <div className="text-xs text-slate-500 mb-4">Three CTAs are placed automatically: after the intro, mid-article, and at the end. Leave any blank to skip.</div>
                <div className="space-y-3">
                  {[
                    ["intro", "After intro"],
                    ["mid", "Mid-article"],
                    ["end", "End of article"],
                  ].map(([key, label]) => (
                    <div key={key} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                      <div className="md:col-span-3 text-xs font-semibold tracking-[0.15em] uppercase text-slate-500">{label}</div>
                      <input value={form[`${key}_cta_text`]} onChange={(e) => setForm({ ...form, [`${key}_cta_text`]: e.target.value })} placeholder="Button text" className={`md:col-span-4 ${inputBase}`} />
                      <input value={form[`${key}_cta_url`]} onChange={(e) => setForm({ ...form, [`${key}_cta_url`]: e.target.value })} placeholder="https://… or /hotels" className={`md:col-span-5 ${inputBase}`} />
                    </div>
                  ))}
                </div>
              </div>

              <FieldRow label="Status" hint="Drafts are hidden from the public site.">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputBase}>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </FieldRow>

              <div className="flex flex-wrap gap-3 pt-6">
                <button type="submit" disabled={saving} data-testid="admin-save-article-btn" className="btn-primary-gradient text-white rounded-full px-7 py-3 font-semibold shadow-md hover:-translate-y-0.5 transition-all inline-flex items-center gap-2 disabled:opacity-60 disabled:translate-y-0">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingId === "new" ? "Publish Article" : "Save Changes"}
                </button>
                <button type="button" onClick={cancel} className="rounded-full px-7 py-3 font-semibold border border-slate-200 hover:bg-slate-50 transition">
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminArticles;
